<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';
import Button from 'primevue/button';

import { UiDialogShell } from '@peanuds/ui';

import {
  getPendingLivenessDialogState,
  openPendingLivenessDialogLink,
  subscribePendingLivenessDialog,
} from './pending-liveness-dialog';
import { useAuthStore } from '../auth0/store/auth.store';

import pendingLivenessDialogIllustration from '../assets/pending-liveness-dialog-illustration-290efe.png';

const PENDING_LIVENESS_DIALOG_TITLE = 'Please confirm your identity';
const PENDING_LIVENESS_DIALOG_MESSAGE =
  'We need to complete a quick security check before you can continue with this action. This usually takes less than a minute.';
const PENDING_LIVENESS_DIALOG_NOTE =
  'This extra verification helps protect your account and confirm that the request is being made by you. Contact support if you have any issues.';

const authStore = useAuthStore();
const dialogState = ref(getPendingLivenessDialogState());

const isVisible = computed(() => dialogState.value.isVisible);
const isConfirmIdentityLoading = computed(() => dialogState.value.launchStatus === 'loading');

let unsubscribe: (() => void) | null = null;

const handleLogout = async () => {
  await authStore.logout();
};

const handleConfirmIdentity = async () => {
  await openPendingLivenessDialogLink();
};

onMounted(() => {
  unsubscribe = subscribePendingLivenessDialog((state) => {
    dialogState.value = state;
  });
});

onUnmounted(() => {
  unsubscribe?.();
});
</script>

<template>
  <UiDialogShell
    :visible="isVisible"
    width="34rem"
    max-width="calc(100vw - 2rem)"
    surface="gradient"
    :show-pattern="false"
    :dismissable-mask="false"
    :close-on-escape="false"
    labelled-by="pending-liveness-dialog-title"
    :pt="{
      mask: {
        class: 'pending-liveness-dialog__mask',
      },
    }"
  >
    <template #surface>
      <div class="pending-liveness-dialog__container">
        <img
          :src="pendingLivenessDialogIllustration"
          alt=""
          class="pending-liveness-dialog__illustration"
        />

        <div class="pending-liveness-dialog__content">
          <h2 id="pending-liveness-dialog-title" class="pending-liveness-dialog__title">
            {{ PENDING_LIVENESS_DIALOG_TITLE }}
          </h2>
          <div class="pending-liveness-dialog__copy">
            <p class="pending-liveness-dialog__message">{{ PENDING_LIVENESS_DIALOG_MESSAGE }}</p>
            <p class="pending-liveness-dialog__message pending-liveness-dialog__message--muted">
              {{ PENDING_LIVENESS_DIALOG_NOTE }}
            </p>
          </div>
        </div>

        <div class="pending-liveness-dialog__actions">
          <Button label="Logout" severity="secondary" rounded @click="handleLogout" />
          <Button
            label="Confirm Identity"
            rounded
            :loading="isConfirmIdentityLoading"
            @click="handleConfirmIdentity"
          />
        </div>
      </div>
    </template>
  </UiDialogShell>
</template>

<style scoped lang="scss">
:global(.pending-liveness-dialog__mask) {
  padding: $space-md;
  background: rgba(15, 23, 42, 0.48);
  backdrop-filter: blur(4px);
}

.pending-liveness-dialog {
  &__container {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: $space-xl;
    padding: 5rem 2.5rem 2.5rem;
  }

  &__illustration {
    width: 100%;
    max-width: 25rem;
    height: 12.375rem;
    object-fit: contain;
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: $space-xl;
    width: 100%;
  }

  &__title {
    margin: 0;
    color: var(--p-surface-700);
    font-size: 1.5rem;
    font-weight: $font-semibold;
    line-height: 1;
    text-align: center;
  }

  &__copy {
    display: flex;
    flex-direction: column;
    gap: $space-xs;
  }

  &__message {
    margin: 0;
    color: var(--p-surface-500);
    font-size: $text-sm;
    font-weight: $font-medium;
    line-height: 1;
    text-align: center;

    &--muted {
      color: var(--p-surface-500);
    }
  }

  &__actions {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: $space-sm;

    :deep(.p-button) {
      min-height: 2rem;
      padding: 0.4375rem 0.65625rem;
      font-size: $text-sm;
      font-weight: $font-medium;
    }
  }
}
</style>
